<script setup>
import { computed, ref, nextTick } from 'vue'
import { State } from 'country-state-city'
import { getAdminDivisionLabel } from '@/helpers/general'
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue'

const props = defineProps({
  location: { type: Object, required: true },
  errors: { type: Array, default: [] },
  editMode: { type: Boolean, default: true },
  hideLabel: { type: Boolean, default: false },
  /** Optional suffix for label (e.g. ' *' for required in grid layout) */
  labelSuffix: { type: String, default: '' },
  labelClass: { type: String, default: 'controller-label' },
  placeholderNon: { type: Boolean, default: false },
  inputClass: { type: String, default: 'input-controller' },
})

const query = defineModel('query', { default: '' })

const triggerRef = ref(null)
const dropdownStyle = ref({})
const placement = ref('bottom')
const DROPDOWN_MAX_HEIGHT = 240 // max-h-60

const adminDivisionLabel = computed(() => getAdminDivisionLabel(props.location.country))

const filteredStates = computed(() => {
  return query.value === ''
    ? State.getStatesOfCountry(props.location.country)
    : State.getStatesOfCountry(props.location.country).filter((state) =>
        state.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')) ||
        state.isoCode
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
})

// Same pattern as BaseListbox: fixed position when opening, optional placement auto
const updateDropdownPosition = () => {
  nextTick(() => {
    const el = triggerRef.value?.$el ?? triggerRef.value

    if (!el) return
    
    const rect = el.getBoundingClientRect()

    const spaceBelow = window.innerHeight - rect.bottom - 4
    placement.value = spaceBelow >= DROPDOWN_MAX_HEIGHT ? 'bottom' : 'top'

    if (placement.value === 'top') {
      dropdownStyle.value = {
        position: 'fixed',
        left: `${rect.left}px`,
        bottom: `${window.innerHeight - rect.top + 4}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
      }
    } else {
      dropdownStyle.value = {
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.bottom + 4}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
      }
    }
  })
}

const onFocus = () => {
  updateDropdownPosition()
}

const onBlur = () => {
  query.value = ''
}
</script>

<template>
  <label class="block">
    <span :class="[labelClass]" v-if="!hideLabel">{{ adminDivisionLabel }}{{ labelSuffix }}</span>
    <template v-if="editMode">
      <Combobox v-model="location.state" v-slot="{ open }">
        <div ref="triggerRef" class="relative">
          <ComboboxInput
            @change="query = $event.target.value"
            @focus="onFocus"
            @blur="onBlur"
            :displayValue="() => location.state ?? ''"
            class="font-normal py-2 w-full"
            :class="[inputClass, { error: errors.includes('state') || errors.includes('stateOrProvince') }]"
            :placeholder="placeholderNon ? '' : adminDivisionLabel"
          />
        </div>
        <small v-if="errors.includes('state') || errors.includes('stateOrProvince')" class="controller-error-msg text-xs">
          <span>This field is required</span>
        </small>
        <Teleport to="body">
          <ComboboxOptions
            static
            v-show="open && filteredStates.length > 0"
            class="border border-tint-1 rounded-lg bg-white shadow-lg max-h-60 overflow-auto text-sm focus:outline-none"
            :style="dropdownStyle"
          >
            <ComboboxOption as="template" v-for="(state, index) in filteredStates" :key="index" v-slot="{ active }" :value="state.name">
              <li
                class="flex items-center justify-between p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
                :class="{ 'bg-blue-50 rounded-lg': active }"
              >
                {{ `${state.name}, ${state.isoCode}` }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </Teleport>
      </Combobox>
    </template>
    <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.state && !location.stateOrProvince }" v-else>{{ location.state || location.stateOrProvince || '-' }}</p>
  </label>
</template>
